import { useState } from 'react';

import { NavLink } from 'react-router-dom';

import PropTypes from 'prop-types';

import { pathMatches } from './navItems';

/**
 * Builds the class string for a nav link's root element.
 *
 * @param {Object} state
 * @param {boolean} state.isActive
 * @param {boolean} state.isDisabled
 * @param {boolean} state.isCollapsed
 * @returns {string}
 */
function getNavLinkClass({ isActive, isDisabled, isCollapsed }) {
	const base = `flex gap-3 px-4 py-3 items-start rounded-lg no-underline transition-all duration-150 ${isCollapsed ? 'justify-center' : ''}`;
	if (isActive && !isDisabled) return `${base} bg-gray-800 text-primary-400 font-medium`;
	return `${base} text-white hover:bg-gray-800 hover:text-primary-400`;
}

/* Renders the icon + label content inside a nav link; `badge` > 0 shows a count (a dot when collapsed) */
const NavLinkContent = ({ Icon, isActive, isDisabled, isCollapsed, label, subText, badge }) => (
	<>
		<span className="relative">
			<Icon className={`text-[1.075rem] ${isActive && !isDisabled ? 'text-primary-400' : 'text-bgBase-600'}`} />
			{isCollapsed && badge > 0 && (
				<span
					className="absolute -right-1.5 -top-1 h-2.5 w-2.5 rounded-full border-2 border-text bg-primary-500"
					aria-label={`${badge} aktiv`}
				/>
			)}
		</span>
		{!isCollapsed && (
			<div className="flex-grow">
				<div className="text-sm text-white">{label}</div>
				<div className="text-xs text-bgBase-600">{subText}</div>
			</div>
		)}
		{!isCollapsed && badge > 0 && (
			<span
				className="self-center rounded-full bg-primary-500 px-1.5 text-[11px] font-bold leading-4 text-white"
				aria-label={`${badge} aktiv`}
			>
				{badge}
			</span>
		)}
	</>
);

NavLinkContent.propTypes = {
	Icon: PropTypes.elementType.isRequired,
	isActive: PropTypes.bool.isRequired,
	isDisabled: PropTypes.bool.isRequired,
	isCollapsed: PropTypes.bool.isRequired,
	label: PropTypes.string.isRequired,
	subText: PropTypes.string.isRequired,
	badge: PropTypes.number,
};

/**
 * Class for a sub-nav link, active when its path matches the current location. A child
 * matches its own path and any nested route, but a more specific sibling wins — so
 * '/post-mailing' stays active on '/post-mailing/:id' yet yields to '/post-mailing/templates'
 * on the Templates route.
 *
 * @param {Array<{to: string}>} children - Sibling nav children.
 * @param {string} pathname - Current location pathname.
 * @param {string} childTo - Path of the child being evaluated.
 * @returns {string}
 */
const getChildLinkClass = (children, pathname, childTo) => {
	const isActive =
		pathMatches(pathname, childTo) &&
		!children.some((sibling) => sibling.to.length > childTo.length && pathMatches(pathname, sibling.to));
	return isActive ? 'text-primary-400 font-medium' : 'text-bgBase-600 hover:text-white';
};

/* Sub-navigation children shown under an expanded parent item */
const SubNavigation = ({ children, pathname }) => (
	<div className="ml-6 mt-1 mb-1 flex flex-col gap-0.5 border-l border-gray-700 pl-3">
		{children.map(({ to: childTo, label: childLabel, icon: ChildIcon }) => (
			<NavLink
				key={childTo}
				to={childTo}
				className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm no-underline transition-colors ${getChildLinkClass(children, pathname, childTo)}`}
			>
				<ChildIcon className="w-3.5 h-3.5" />
				{childLabel}
			</NavLink>
		))}
	</div>
);

SubNavigation.propTypes = {
	children: PropTypes.arrayOf(
		PropTypes.shape({
			to: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			icon: PropTypes.elementType.isRequired,
		}),
	).isRequired,
	pathname: PropTypes.string.isRequired,
};

/* Tooltip shown when sidebar is collapsed and a nav item is hovered */
const CollapsedTooltip = ({ item, subText, pathname }) => {
	const { label, children } = item;

	return (
		<div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg whitespace-nowrap z-50 shadow-xl border border-gray-700">
			<div className="font-medium">{label}</div>
			{children ? (
				<div className="flex flex-col gap-1 mt-2 pt-2 border-t border-gray-700">
					{children.map(({ to: childTo, label: childLabel }) => (
						<NavLink
							key={childTo}
							to={childTo}
							className={`text-xs no-underline transition-colors ${getChildLinkClass(children, pathname, childTo)}`}
						>
							{childLabel}
						</NavLink>
					))}
				</div>
			) : (
				<div className="text-xs text-bgBase-600 mt-1">{subText}</div>
			)}
			<div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-gray-800" />
		</div>
	);
};

CollapsedTooltip.propTypes = {
	item: PropTypes.shape({
		label: PropTypes.string.isRequired,
		children: PropTypes.arrayOf(
			PropTypes.shape({
				to: PropTypes.string.isRequired,
				label: PropTypes.string.isRequired,
			}),
		),
	}).isRequired,
	subText: PropTypes.string.isRequired,
	pathname: PropTypes.string.isRequired,
};

/**
 * Derives access/lock/link state for a nav item.
 * upsellLocked items (no access + upsellWhenLocked) stay clickable and route to the
 * upsell instead of being hard-disabled, so users can still discover the feature.
 */
function deriveNavState(item, caps) {
	const { to, sub, disabled, requires, children, upsellWhenLocked } = item;

	const reqArr = Array.isArray(requires) ? requires : [requires].filter(Boolean);
	const hasAccess = reqArr.length === 0 || reqArr.every((r) => caps.includes(r));
	const upsellLocked = !hasAccess && Boolean(upsellWhenLocked);
	const isDisabled = (disabled || !hasAccess) && !upsellLocked;

	let subText = sub;
	if (upsellLocked) subText = 'Testphase starten';
	else if (!hasAccess) subText = 'Nicht in Ihrem Paket enthalten';

	let linkTarget = to;
	if (upsellLocked) linkTarget = '/not-allowed';
	else if (isDisabled) linkTarget = '#';
	else if (children) linkTarget = children[0].to;

	// Pass the gated add-on key (derived from the `<key>.access` requirement) so the
	// not-allowed page shows that add-on's upsell.
	const addonReq = reqArr.find((r) => typeof r === 'string' && r.endsWith('.access'));
	const addonKey = addonReq ? addonReq.slice(0, -'.access'.length) : null;

	return {
		hasAccess,
		upsellLocked,
		isDisabled,
		subText,
		linkTarget,
		linkState: upsellLocked && addonKey ? { addon: addonKey } : undefined,
	};
}

/* Single nav item */
const NavItem = ({ item, caps, isCollapsed, pathname, badge = 0 }) => {
	const { to, icon: Icon, label, external, children } = item;
	const [isHovered, setIsHovered] = useState(false);

	const { upsellLocked, isDisabled, subText, linkTarget, linkState } = deriveNavState(item, caps);
	const isParentActive = children ? pathname.startsWith(to) : false;

	const content = (isActive) => (
		<NavLinkContent
			Icon={Icon}
			isActive={isActive}
			isDisabled={isDisabled}
			isCollapsed={isCollapsed}
			label={label}
			subText={subText}
			badge={badge}
		/>
	);

	return (
		<div className="relative" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
			{external ? (
				<a
					href={to}
					target="_blank"
					rel="noopener noreferrer"
					className={getNavLinkClass({ isActive: false, isDisabled, isCollapsed })}
				>
					{content(false)}
				</a>
			) : (
				<NavLink
					to={linkTarget}
					state={linkState}
					className={({ isActive }) =>
						getNavLinkClass({
							isActive: children && !upsellLocked ? isParentActive : isActive,
							isDisabled,
							isCollapsed,
						})
					}
					style={isDisabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
					onClick={isDisabled ? (e) => e.preventDefault() : undefined}
					aria-disabled={isDisabled}
				>
					{({ isActive }) => content(children ? isParentActive : isActive)}
				</NavLink>
			)}

			{children && !isCollapsed && isParentActive && !isDisabled && !upsellLocked && (
				<SubNavigation pathname={pathname}>{children}</SubNavigation>
			)}

			{isCollapsed && isHovered && <CollapsedTooltip item={item} subText={subText} pathname={pathname} />}
		</div>
	);
};

NavItem.propTypes = {
	item: PropTypes.shape({
		to: PropTypes.string.isRequired,
		icon: PropTypes.elementType.isRequired,
		label: PropTypes.string.isRequired,
		sub: PropTypes.string,
		disabled: PropTypes.bool,
		requires: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
		upsellWhenLocked: PropTypes.bool,
		external: PropTypes.bool,
		children: PropTypes.arrayOf(
			PropTypes.shape({
				to: PropTypes.string.isRequired,
				label: PropTypes.string.isRequired,
				icon: PropTypes.elementType.isRequired,
			}),
		),
	}).isRequired,
	caps: PropTypes.arrayOf(PropTypes.string).isRequired,
	isCollapsed: PropTypes.bool.isRequired,
	pathname: PropTypes.string.isRequired,
	badge: PropTypes.number,
};

export default NavItem;
